import { StateManager } from '../../managers';
import type { StateKey, GameState } from '../../types';

export abstract class UIComponent<TLocalState = unknown> {
  protected rootEl: HTMLElement | null = null;
  protected subscriptions: Array<() => void> = [];
  protected localState: TLocalState | null = null;

  constructor(protected config: { rootId: string }) {}

  initialize(): void {
    this.rootEl = document.getElementById(this.config.rootId);
    if (!this.rootEl) {
      console.warn(`[UIComponent] Element #${this.config.rootId} not found`);
      return;
    }

    this.cacheElements();
    this.bindEvents();
    this.setupSubscriptions();
  }

  destroy(): void {
    this.subscriptions.forEach((unsub) => unsub());
    this.subscriptions = [];
  }

  show(): void {
    this.rootEl?.classList.remove('hidden');
  }

  hide(): void {
    this.rootEl?.classList.add('hidden');
  }

  setLoading(loading: boolean): void {
    if (loading) {
      this.rootEl?.classList.add('loading');
    } else {
      this.rootEl?.classList.remove('loading');
    }
  }

  protected abstract cacheElements(): void;
  protected abstract bindEvents(): void;
  protected abstract setupSubscriptions(): void;
  protected abstract onRender(): void;

  protected subscribeState<K extends StateKey>(
    key: K,
    callback: (value: GameState[K] | undefined) => void
  ): void {
    const unsub = StateManager.subscribe(key, callback);
    this.subscriptions.push(unsub);
  }

  protected $(selector: string): HTMLElement | null {
    return this.rootEl?.querySelector(selector) ?? null;
  }

  protected $$(selector: string): NodeListOf<HTMLElement> {
    return this.rootEl?.querySelectorAll(selector) ?? ([] as unknown as NodeListOf<HTMLElement>);
  }
}
